<template>
	<Skeleton :paragraph="{ rows: 8 }" :title="false" :loading="loading">
		<VerticalLayout>
			<Tooltip
				v-if="licenseAllowGp && !cssrGpCanEnable"
				:title="$t('settings.vrsSettingsForm.pppRtk.cssrGpEnabledTooltip')"
				placement="topLeft"
			>
				<LabeledSwitch
					v-if="licenseAllowGp"
					v-model="cssrGpEnabled"
					:disabled="!cssrGpCanEnable || settings.deleted"
					:rightLabel="$t('settings.vrsSettingsForm.pppRtk.cssrGpEnabled')"
					data-testid="settings-subnetwork-atmospheric-switch"
					@change="onUpdateField('cssrGpEnabled', $event)"
				/>
			</Tooltip>
			<LabeledSwitch
				v-else-if="licenseAllowGp"
				v-model="cssrGpEnabled"
				:disabled="!cssrGpCanEnable || settings.deleted"
				:rightLabel="$t('settings.vrsSettingsForm.pppRtk.cssrGpEnabled')"
				data-testid="settings-subnetwork-atmospheric-switch"
				@change="onUpdateField('cssrGpEnabled', $event)"
			/>
			<TypographyParagraph v-if="!cssrGpEnabled && cnetDataRef.grids.length > 0">
				{{ $t('settings.vrsSettingsForm.pppRtk.atmosphericCorrectionsDisabledHint') }}
			</TypographyParagraph>
			<TypographyParagraph>
				{{ $t('settings.vrsSettingsForm.pppRtk.modesHint1') }}
				(<abbr title="Phase center offset">PCO</abbr>)
				{{ $t('settings.vrsSettingsForm.pppRtk.modesHint2') }}
			</TypographyParagraph>
			<TypographyParagraph v-if="featureFlagCnetConstructor">
				{{ $t('settings.vrsSettingsForm.pppRtk.cnetConstructorHint') }}
				<router-link :to="{ name: 'cnet-constructor' }">
					{{ $t('settings.vrsSettingsForm.pppRtk.cnetConstructorLink') }}
				</router-link>
			</TypographyParagraph>

			<div class="scrollable-x">
				<Table
					aria-label="cnet list"
					:columns="columns"
					:data-source="cnetDataRef.grids"
					:pagination="false"
					size="small"
				>
					<template #bodyCell="{ column, record }">
						<template v-if="column.dataIndex === 'cnet_source_id'">
							<router-link v-if="record.cnet_source_id != null" :to="{ name: 'cnet-constructor-edit', params: { id: record.cnet_source_id } }">
								#{{ record.cnet_source_id }}
							</router-link>
							<span v-else>—</span>
						</template>
						<template v-else-if="column.dataIndex === 'actions'">
							<Space>
								<Button
									type="link"
									size="small"
									:disabled="settings.deleted"
									@click="onEditCNet(record)"
								>
									{{ $t('settings.vrsSettingsForm.pppRtk.cnetUpdateForm.editCNetLabel') }}
								</Button>
								<PopconfirmButton
									:title="deleteConfirmationTitle"
									:ok-text="$t('settings.vrsSettingsForm.pppRtk.deleteCNetConfirm')"
									:cancel-text="$t('common.cancelButtonLabel')"
									:onConfirm="() => onDeleteCNet(record.id)"
									size="small"
									:disabled="settings.deleted"
								>
									{{ $t('settings.vrsSettingsForm.pppRtk.deleteCNetLabel') }}
								</PopconfirmButton>
							</Space>
						</template>
					</template>
					<template #emptyText>
						{{ $t('settings.vrsSettingsForm.pppRtk.cnetNotConfigured') }}
					</template>
				</Table>
			</div>

			<HorizontalLayout>
				<Button
					v-if="featureFlagCnetConstructor"
					type="primary"
					:label="$t('settings.vrsSettingsForm.pppRtk.uploadCNetLabel')"
					@click.prevent="openCNetListModal"
				>
					{{ $t('settings.vrsSettingsForm.pppRtk.cnetAddFromListLabel') }}
				</Button>
				<UploadButton
					buttonType="default"
					:label="$t('settings.vrsSettingsForm.pppRtk.uploadCNetLabel')"
					label-visibility="always"
					:data="{id: settings.id}"
					buttonClass="width-min"
					action="vrs/sub_network/cnet/json/upload/start/"
					:onSuccess="handleUploadSuccessStart"
					:disabled="settings.deleted"
				/>
				<PopconfirmButton
					v-if="cnetDataRef.grids.length > 0"
					:title="clearConfirmationTitle"
					:ok-text="$t('settings.vrsSettingsForm.pppRtk.clearCNetConfirm')"
					:cancel-text="$t('common.cancelButtonLabel')"
					:onConfirm="onClearCNet"
				>
					{{ $t('settings.vrsSettingsForm.pppRtk.clearCNetLabel') }}
				</PopconfirmButton>
				<Button v-if="cnetDataRef.grids.length > 0" @click.prevent="onDownloadAll">
					<DownloadOutlined /> {{ $t('settings.vrsSettingsForm.pppRtk.cnetDownloadAllLabel') }}
				</Button>
			</HorizontalLayout>
		</VerticalLayout>
		<CNetListModal
			v-model="cnetListModalVisible"
			:subNetworkId="settings.id"
			:fetchSettings="fetchSettings"
		/>
		<CNetUpdateModal
			v-model="editModalVisible"
			:grid="selectedGrid"
			:fetchSettings="fetchSettings"
		/>
		<ConfirmCnetImportModal
			v-model="confirmCnetImportModalVisible"
			:subNetworkId="settings.id"
			:importData="cnetImportData"
			:onSuccess="onCnetImportSuccess"
		/>
	</Skeleton>
</template>

<script>
import { ref, toRef, computed, watch } from 'vue';
import { Skeleton, TypographyParagraph, Table, Space, Button, message, Tooltip } from 'ant-design-vue';
import { DownloadOutlined } from '@ant-design/icons-vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';

import TranslationUtils from '@/i18n/TranslationUtils';

import { useBaseStationsStore } from '@/stores/baseStations';
import { useLicenseStore } from '@/stores/licenseStore';

import { showErrorMessage } from '@/components/common/errorMessage';

import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';
import UploadButton from '@/components/common/UploadButton.vue';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import HorizontalLayout from '@/components/common/HorizontalLayout.vue';

import CNetUpdateModal from '@/components/settings/subNetworks/CNetUpdateModal.vue';
import CNetListModal from '@/components/settings/subNetworks/CNetListModal.vue';
import ConfirmCnetImportModal from '@/components/settings/subNetworks/ConfirmCnetImportModal.vue';

import {
	clearConfirmationKey,
	deleteConfirmationKey,
} from '@/components/settings/subNetworks/pppRtkSettingsFormUtils';

export default {
	components: {
		Skeleton, TypographyParagraph, Table, Space, Button, Tooltip,
		LabeledSwitch, VerticalLayout, UploadButton, PopconfirmButton, DownloadOutlined, HorizontalLayout,
		CNetUpdateModal, CNetListModal, ConfirmCnetImportModal,
	},
	props: ['loading', 'settings', 'cnetData', 'onUpdateField', 'fetchSettings'],
	setup(props) {
		const { t } = useI18n();
		const cssrGpEnabled = ref(false);
		const settingsRef = toRef(props, 'settings');
		const cnetDataRef = toRef(props, 'cnetData');
		const editModalVisible = ref(false);
		const cnetListModalVisible = ref(false);
		const selectedGrid = ref(null);
		const confirmCnetImportModalVisible = ref(false);
		const cnetImportData = ref({});

		const openCNetListModal = () => {
			cnetListModalVisible.value = true;
		}
		const handleUploadSuccess = () => {
			props.fetchSettings();
			useBaseStationsStore().baseStationRefreshList();
		}
		const handleUploadSuccessStart = response => {
			confirmCnetImportModalVisible.value = true;
			cnetImportData.value = response;
		}
		const onCnetImportSuccess = () => {
			props.fetchSettings();
			useBaseStationsStore().baseStationRefreshList();
		}
		const onClearCNet = () => {
			Api.subNetworkCNetClear(settingsRef.value.id, response => {
				if (response.status === 'ok') {
					message.success(t('settings.vrsSettingsForm.pppRtk.clearCNetSuccess'));
					props.fetchSettings();
				} else {
					showErrorMessage(response.errors)
				}
			});
		}
		const onDeleteCNet = (gridpointNetId) => {
			Api.subNetworkCNetDelete(gridpointNetId, response => {
				if (response.status === 'ok') {
					message.success(t('settings.vrsSettingsForm.pppRtk.deleteCNetSuccess'));
					props.fetchSettings();
				} else {
					showErrorMessage(response.errors)
				}
			});
		}
		const onDownloadAll = () => Api.subNetworkCNetDownloadAll();
		const onEditCNet = (grid) => {
			selectedGrid.value = grid;
			editModalVisible.value = true;
		};

		const licenseStore = useLicenseStore();
		const licenseAllowGp = computed(() => licenseStore.allowPppGp);
		const cssrGpCanEnable = computed(() => (
			Boolean(settingsRef.value.cssrBrdcEnabled) && cnetDataRef.value.grids?.length > 0
		));
		const featureFlagCnetConstructor = computed(() => licenseStore.featureFlagCnetConstructor);
		const columnsConsts = computed(() => {
			const columns = [
				{ dataIndex: 'cnet_number' },
				{ dataIndex: 'gridpoint_count' },
				{ dataIndex: 'week' },
			]
			if (featureFlagCnetConstructor.value) {
				columns.push({ dataIndex: 'cnet_source_id' });
			}
			columns.push({ dataIndex: 'actions' });
			return columns;
		});

		const columns = TranslationUtils.addTranslatedField(
			columnsConsts,
			'dataIndex', 'title',
			'settings.vrsSettingsForm.pppRtk.tableColumns',
		);

		const deleteConfirmationTitle = computed(() => t(
			'settings.vrsSettingsForm.pppRtk.' + deleteConfirmationKey({
				cssrGpEnabled: settingsRef.value.cssrGpEnabled,
				grids: cnetDataRef.value.grids,
			}),
		));
		const clearConfirmationTitle = computed(() => t(
			'settings.vrsSettingsForm.pppRtk.' + clearConfirmationKey({
				cssrGpEnabled: settingsRef.value.cssrGpEnabled,
			}),
		));

		watch(settingsRef, value => {
			cssrGpEnabled.value = value.cssrGpEnabled;
		});

		return {
			cssrGpEnabled,
			licenseAllowGp,
			cssrGpCanEnable,
			cnetDataRef,
			handleUploadSuccess,
			handleUploadSuccessStart,
			onCnetImportSuccess,
			onClearCNet,
			onDeleteCNet,
			onDownloadAll,
			onEditCNet,
			cnetListModalVisible,
			openCNetListModal,
			editModalVisible,
			selectedGrid,
			confirmCnetImportModalVisible,
			cnetImportData,
			columns,
			featureFlagCnetConstructor,
			deleteConfirmationTitle,
			clearConfirmationTitle,
		};
	}
}
</script>
